import { FilteredItemProps } from '@delmonicos/shared-design-system-react/src/components/filtered-item/filtered-item';
import { GetRolesPaginatedModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/roles/models/inputs/get-roles-paginated.model';
import { GatewayRoleModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/roles/models/output/gateway-role.model';
import {
  ColumnDef,
  ColumnOrderState,
  VisibilityState,
  createColumnHelper,
} from '@tanstack/react-table';

import { CustomerFilterStateController } from '@app/feature/customer/customer-filter-state/customer-filter-state.controller';
import { NetworkFilterStateController } from '@app/feature/network/network-filter-state/network-filter-state.controller';

import { RoleListCustomerColumn } from './role-list-customer.column';
import { RoleListDescriptionColumn } from './role-list-description.column';
import { RoleListIdColumn } from './role-list-id.column';
import { RoleListNameColumn } from './role-list-name.column';

// /////////////////////////////////////////////////////////////////////////////
// SORTING
// /////////////////////////////////////////////////////////////////////////////

export const ROLE_LIST_SORT_FIELD: NonNullable<
  GetRolesPaginatedModel['sortField']
> = 'createdAt';

export const ROLE_LIST_SORT_ORDER: NonNullable<
  GetRolesPaginatedModel['sortOrder']
> = 1;

// /////////////////////////////////////////////////////////////////////////////
// FILTERING
// /////////////////////////////////////////////////////////////////////////////

type ResourceListFilter = Partial<
  Record<
    keyof GetRolesPaginatedModel,
    {
      componentType: React.ComponentType<{
        value: any;
        onRemove: FilteredItemProps['onRemove'];
      }>;
    }
  >
>;

export const ROLE_LIST_FILTER: ResourceListFilter = {
  customerId: {
    componentType: CustomerFilterStateController,
  },
  networkId: {
    componentType: NetworkFilterStateController,
  },
} as const;

export const ROLE_LIST_FILTER_NAME_LIST = Object.keys(
  ROLE_LIST_FILTER,
) as Array<keyof typeof ROLE_LIST_FILTER>;

// /////////////////////////////////////////////////////////////////////////////
// DISPLAYING
// /////////////////////////////////////////////////////////////////////////////

export const roleListColumnHelper = createColumnHelper<GatewayRoleModel>();

type ResourceColumnType = ColumnDef<GatewayRoleModel, any>[];

export const ROLE_LIST_COLUMN_LIST: ResourceColumnType = [
  roleListColumnHelper.accessor('id', RoleListIdColumn),
  roleListColumnHelper.accessor('name', RoleListNameColumn),
  roleListColumnHelper.accessor('customer', RoleListCustomerColumn),
  roleListColumnHelper.accessor('description', RoleListDescriptionColumn),
];

export const ROLE_LIST_COLUMN_ID_LIST = ROLE_LIST_COLUMN_LIST.flatMap(
  (columnDef) => {
    if ('accessorKey' in columnDef) {
      return [columnDef.id ?? (columnDef.accessorKey as string)];
    }

    if (columnDef.id) {
      return [columnDef.id];
    }

    return [];
  },
);

export const ROLE_LIST_COLUMN_ORDER = [...ROLE_LIST_COLUMN_ID_LIST];

export const ROLE_LIST_COLUMN_VISIBILITY: VisibilityState =
  ROLE_LIST_COLUMN_ID_LIST.reduce(
    (columnVisibility, columnId) => ({
      ...columnVisibility,
      [columnId]: true,
    }),
    {} as VisibilityState,
  );

// /////////////////////////////////////////////////////////////////////////////
// OPTIONS
// /////////////////////////////////////////////////////////////////////////////

export interface RoleListOptionsModel {
  columnVisibility: VisibilityState;
  columnOrder: ColumnOrderState;
}

export const ROLE_LIST_DEFAULT_OPTIONS: RoleListOptionsModel = {
  columnVisibility: ROLE_LIST_COLUMN_VISIBILITY,
  columnOrder: ROLE_LIST_COLUMN_ORDER,
};

export const ROLE_LIST_COLUMN_LABEL_I18N_KEY: Record<string, string> = {
  id: 'delmonicos-shared-sdk.outputs-base.id',
  name: 'delmonicos-shared-sdk.outputs-role-template.name',
  customer: 'delmonicos-shared-sdk.outputs-gateway-role.customer',
  description: 'delmonicos-shared-sdk.outputs-role-template.description',
};
